import "bootstrap/dist/css/bootstrap.min.css";
import { get, ref } from "firebase/database";
import { useEffect, useState } from "react";
import { db } from "../Firebase";
import { exportToExcel, formatFamilyDataForExcel, formatMemberDataForExcel } from '../utils/excelExport';

const AdvancedAnalytics = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    state: "",
    city: "",
    block: "",
    areaType: "",
    status: "",
    workType: "",
    workArea: "",
    educationStatus: "",
    gender: "",
    relation: "",
    ageRange: "",
    bloodGroup: "",
    maritalStatus: "",
    jatiPrakar: "",
    upjatiPrakar: "",
    gotra: "",
    moor: "",
    mandir: "",
    mandirRelation: "",
    familyStatus: "",
    houseStatus: "",
    motherTongue: ""
  });

  // View mode
  const [viewMode, setViewMode] = useState("families"); // families, members, analytics

  // Analytics data
  const [analytics, setAnalytics] = useState({});

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [allData, filters, viewMode]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Check if Firebase is initialized
      if (!db) {
        console.error('Firebase is not initialized. Please check your .env configuration.');
        alert('Firebase configuration error. Please check console for details.');
        return;
      }
      
      const snap = await get(ref(db, 'registration'));
      if (snap.exists()) {
        const data = Object.entries(snap.val()).map(([id, family]) => ({
          id,
          ...family,
          members: family.members ? Object.entries(family.members).map(([mid, member]) => ({
            id: mid,
            familyId: id,
            ...member
          })) : []
        }));
        setAllData(data);
        calculateAnalytics(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.message.includes('Firebase')) {
        alert('Firebase configuration error. Please create a .env file with your Firebase settings.');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (data) => {
    const stats = {
      totalFamilies: data.length,
      totalMembers: data.reduce((sum, family) => sum + (family.members?.length || 0), 0),
      approvedFamilies: data.filter(f => f.meta?.status === 'approved').length,
      pendingFamilies: data.filter(f => f.meta?.status === 'pending' || !f.meta?.status).length,
      stateWise: {},
      workTypeWise: {},
      educationWise: {},
      genderWise: { पुरुष: 0, महिला: 0, अन्य: 0 },
      ageGroups: { '0-18': 0, '19-35': 0, '36-60': 0, '60+': 0 }
    };

    data.forEach(family => {
      // State wise count
      const state = family.basic?.state;
      if (state) {
        stats.stateWise[state] = (stats.stateWise[state] || 0) + 1;
      }

      // Member analytics
      family.members?.forEach(member => {
        // Gender
        if (member.gender) {
          stats.genderWise[member.gender] = (stats.genderWise[member.gender] || 0) + 1;
        }

        // Work type
        if (member.workType) {
          stats.workTypeWise[member.workType] = (stats.workTypeWise[member.workType] || 0) + 1;
        }

        // Education
        if (member.educationStatus) {
          stats.educationWise[member.educationStatus] = (stats.educationWise[member.educationStatus] || 0) + 1;
        }

        // Age calculation
        if (member.dob) {
          const age = new Date().getFullYear() - new Date(member.dob).getFullYear();
          if (age <= 18) stats.ageGroups['0-18']++;
          else if (age <= 35) stats.ageGroups['19-35']++;
          else if (age <= 60) stats.ageGroups['36-60']++;
          else stats.ageGroups['60+']++;
        }
      });
    });

    setAnalytics(stats);
  };

  const applyFilters = () => {
    let filtered = [...allData];

    if (viewMode === "members") {
      // Flatten to member view
      filtered = allData.flatMap(family => 
        family.members?.map(member => ({
          ...member,
          familyState: family.basic?.state,
          familyCity: family.basic?.city,
          familyBlock: family.basic?.block,
          familyAreaType: family.basic?.areaType,
          familyStatus: family.meta?.status,
          headName: family.basic?.headName,
          familyAddress: family.basic?.address,
          familyPanchayat: family.basic?.panchayat,
          familyGram: family.basic?.gram,
          familyUrbanArea: family.basic?.urbanArea,
          familyTotalMembers: family.basic?.Totalmember,
          familyPopularName: family.basic?.displayName,
          familyMobile: family.basic?.mobile,
          familyLanguage: family.basic?.language,
          familyMandir: family.basic?.Mandirtype,
          familyMandirRelation: family.basic?.mandirRelation,
          familyJatiPrakar: family.basic?.jatiPrakar,
          familyUpjatiPrakar: family.basic?.upjatiPrakar,
          familyGotra: family.basic?.gotra,
          familyMoor: family.basic?.moor,
          familyWorkType: family.basic?.workType,
          familyWorkArea: family.basic?.workArea,
          familyType: family.basic?.familyType,
          familyHouseType: family.basic?.houseType,
          familyId: family.id,
          registrationDate: family.meta?.createdAt
        })) || []
      );

      // Apply member filters
      if (filters.gender) filtered = filtered.filter(m => m.gender === filters.gender);
      if (filters.relation) filtered = filtered.filter(m => m.relation === filters.relation);
      if (filters.workType) filtered = filtered.filter(m => m.workType === filters.workType);
      if (filters.workArea) filtered = filtered.filter(m => m.workArea === filters.workArea);
      if (filters.educationStatus) filtered = filtered.filter(m => m.educationStatus === filters.educationStatus);
      if (filters.bloodGroup) filtered = filtered.filter(m => m.bloodGroup === filters.bloodGroup);
      if (filters.maritalStatus) filtered = filtered.filter(m => m.maritalStatus === filters.maritalStatus);
      if (filters.state) filtered = filtered.filter(m => m.familyState === filters.state);
      if (filters.city) filtered = filtered.filter(m => m.familyCity === filters.city);
      if (filters.block) filtered = filtered.filter(m => m.familyBlock === filters.block);
      if (filters.areaType) filtered = filtered.filter(m => m.familyAreaType === filters.areaType);
      if (filters.status) filtered = filtered.filter(m => m.familyStatus === filters.status);
    } else {
      // Family view filters
      if (filters.state) filtered = filtered.filter(f => f.basic?.state === filters.state);
      if (filters.city) filtered = filtered.filter(f => f.basic?.city === filters.city);
      if (filters.block) filtered = filtered.filter(f => f.basic?.block === filters.block);
      if (filters.areaType) filtered = filtered.filter(f => f.basic?.areaType === filters.areaType);
      if (filters.status) filtered = filtered.filter(f => f.meta?.status === filters.status);
      if (filters.jatiPrakar) filtered = filtered.filter(f => f.basic?.jatiPrakar === filters.jatiPrakar);
      if (filters.upjatiPrakar) filtered = filtered.filter(f => f.basic?.upjatiPrakar === filters.upjatiPrakar);
      if (filters.gotra) filtered = filtered.filter(f => f.basic?.gotra === filters.gotra);
      if (filters.moor) filtered = filtered.filter(f => f.basic?.moor === filters.moor);
      if (filters.mandir) filtered = filtered.filter(f => f.basic?.Mandirtype === filters.mandir);
      if (filters.mandirRelation) filtered = filtered.filter(f => f.basic?.mandirRelation === filters.mandirRelation);
      if (filters.familyStatus) filtered = filtered.filter(f => f.basic?.familyType === filters.familyStatus);
      if (filters.houseStatus) filtered = filtered.filter(f => f.basic?.houseType === filters.houseStatus);
      if (filters.motherTongue) filtered = filtered.filter(f => f.basic?.language === filters.motherTongue);
      if (filters.workType) filtered = filtered.filter(f => f.basic?.workType === filters.workType);
      if (filters.workArea) filtered = filtered.filter(f => f.basic?.workArea === filters.workArea);
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      state: "", city: "", block: "", areaType: "", status: "", workType: "", workArea: "",
      educationStatus: "", gender: "", relation: "", ageRange: "",
      bloodGroup: "", maritalStatus: "", jatiPrakar: "", upjatiPrakar: "",
      gotra: "", moor: "", mandir: "", mandirRelation: "", familyStatus: "",
      houseStatus: "", motherTongue: ""
    });
  };

  const exportData = () => {
    if (viewMode === "members") {
      const formattedData = formatMemberDataForExcel(filteredData);
      exportToExcel(formattedData, 'members_data', 'Members Data', {
        minColumnWidth: 15,
        maxColumnWidth: 30
      });
    } else {
      const formattedData = formatFamilyDataForExcel(filteredData);
      exportToExcel(formattedData, 'families_data', 'Families Data', {
        minColumnWidth: 15,
        maxColumnWidth: 30
      });
    }
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (key) => {
    const values = new Set();
    if (viewMode === "members") {
      allData.forEach(family => {
        family.members?.forEach(member => {
          if (key.startsWith('family')) {
            const familyKey = key.replace('family', '').toLowerCase();
            if (family.basic?.[familyKey]) values.add(family.basic[familyKey]);
          } else if (member[key]) {
            values.add(member[key]);
          }
        });
      });
    } else {
      allData.forEach(family => {
        if (family.basic?.[key]) values.add(family.basic[key]);
        if (family.meta?.[key]) values.add(family.meta[key]);
      });
    }
    return Array.from(values).sort();
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h4 className="text-primary mb-3">📊 Advanced Data Analytics</h4>
          
          {/* View Mode Selector */}
          <div className="btn-group mb-3" role="group">
            <button 
              className={`btn ${viewMode === 'families' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('families')}
            >
              👨👩👧👦 Families View
            </button>
            <button 
              className={`btn ${viewMode === 'members' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('members')}
            >
              👤 Members View
            </button>
            <button 
              className={`btn ${viewMode === 'analytics' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('analytics')}
            >
              📈 Analytics
            </button>
          </div>

          {/* Quick Stats */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card bg-primary text-dark">
                <div className="card-body text-center">
                  <h5>{analytics.totalFamilies || 0}</h5>
                  <small>Total Famalies</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success dark">
                <div className="card-body text-center">
                  <h5>{analytics.totalMembers || 0}</h5>
                  <small>Total Members</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info dark">
                <div className="card-body text-center">
                  <h5>{analytics.approvedFamilies || 0}</h5>
                  <small>Approved Families</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning dark">
                <div className="card-body text-center">
                  <h5>{analytics.pendingFamilies || 0}</h5>
                  <small>Pending Families</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {viewMode !== 'analytics' && (
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h6 className="text-secondary mb-3">🔍 Filters</h6>
            <div className="row g-3">
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.state} onChange={(e) => handleFilterChange('state', e.target.value)}>
                  <option value="">All States</option>
                  {getUniqueValues('state').map(state => <option key={state} value={state}>{state}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)}>
                  <option value="">All Cities</option>
                  {getUniqueValues('city').map(city => <option key={city} value={city}>{city}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.block} onChange={(e) => handleFilterChange('block', e.target.value)}>
                  <option value="">All Blocks</option>
                  {getUniqueValues('block').map(block => <option key={block} value={block}>{block}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.areaType} onChange={(e) => handleFilterChange('areaType', e.target.value)}>
                  <option value="">All Areas</option>
                  <option value="Urban">Urban</option>
                  <option value="Rural">Rural</option>
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  <option value="">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="hold">Hold</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.workType} onChange={(e) => handleFilterChange('workType', e.target.value)}>
                  <option value="">All Work Types</option>
                  {getUniqueValues('workType').map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              
              {/* Second Row */}
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.workArea} onChange={(e) => handleFilterChange('workArea', e.target.value)}>
                  <option value="">All Work Areas</option>
                  {getUniqueValues('workArea').map(area => <option key={area} value={area}>{area}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.jatiPrakar} onChange={(e) => handleFilterChange('jatiPrakar', e.target.value)}>
                  <option value="">All Jati Prakar</option>
                  {getUniqueValues('jatiPrakar').map(jp => <option key={jp} value={jp}>{jp}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.upjatiPrakar} onChange={(e) => handleFilterChange('upjatiPrakar', e.target.value)}>
                  <option value="">All Upjati Prakar</option>
                  {getUniqueValues('upjatiPrakar').map(up => <option key={up} value={up}>{up}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.gotra} onChange={(e) => handleFilterChange('gotra', e.target.value)}>
                  <option value="">All Gotra</option>
                  {getUniqueValues('gotra').map(gotra => <option key={gotra} value={gotra}>{gotra}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.mandir} onChange={(e) => handleFilterChange('mandir', e.target.value)}>
                  <option value="">All Mandirs</option>
                  {getUniqueValues('Mandirtype').map(mandir => <option key={mandir} value={mandir}>{mandir}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filters.motherTongue} onChange={(e) => handleFilterChange('motherTongue', e.target.value)}>
                  <option value="">All Languages</option>
                  {getUniqueValues('language').map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
              </div>
              
              {viewMode === 'members' && (
                <>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={filters.gender} onChange={(e) => handleFilterChange('gender', e.target.value)}>
                      <option value="">All Genders</option>
                      <option value="पुरुष">पुरुष</option>
                      <option value="महिला">महिला</option>
                      <option value="अन्य">अन्य</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={filters.relation} onChange={(e) => handleFilterChange('relation', e.target.value)}>
                      <option value="">All Relations</option>
                      {getUniqueValues('relation').map(rel => <option key={rel} value={rel}>{rel}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={filters.bloodGroup} onChange={(e) => handleFilterChange('bloodGroup', e.target.value)}>
                      <option value="">All Blood Groups</option>
                      {getUniqueValues('bloodGroup').map(bg => <option key={bg} value={bg}>{bg}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={filters.maritalStatus} onChange={(e) => handleFilterChange('maritalStatus', e.target.value)}>
                      <option value="">All Marital Status</option>
                      {getUniqueValues('maritalStatus').map(ms => <option key={ms} value={ms}>{ms}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={filters.educationStatus} onChange={(e) => handleFilterChange('educationStatus', e.target.value)}>
                      <option value="">All Education</option>
                      {getUniqueValues('educationStatus').map(es => <option key={es} value={es}>{es}</option>)}
                    </select>
                  </div>
                </>
              )}
              
              <div className="col-md-2">
                <button className="btn btn-outline-secondary btn-sm w-100" onClick={clearFilters}>Clear All</button>
              </div>
              <div className="col-md-2">
                <button className="btn btn-success btn-sm w-100" onClick={exportData}>📥 Export</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Loading data...</p>
        </div>
      ) : (
        <>
          {viewMode === 'analytics' && (
            <div className="row g-4">
              {/* State wise distribution */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6>State wise Distribution</h6>
                  </div>
                  <div className="card-body">
                    {Object.entries(analytics.stateWise || {}).map(([state, count]) => (
                      <div key={state} className="d-flex justify-content-between mb-2">
                        <span>{state}</span>
                        <span className="badge bg-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Work type distribution */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6>Work Type Distribution</h6>
                  </div>
                  <div className="card-body">
                    {Object.entries(analytics.workTypeWise || {}).map(([type, count]) => (
                      <div key={type} className="d-flex justify-content-between mb-2">
                        <span>{type}</span>
                        <span className="badge bg-success">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Gender distribution */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6>Gender Distribution</h6>
                  </div>
                  <div className="card-body">
                    {Object.entries(analytics.genderWise || {}).map(([gender, count]) => (
                      <div key={gender} className="d-flex justify-content-between mb-2">
                        <span>{gender}</span>
                        <span className="badge bg-info">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Age groups */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h6>Age Group Distribution</h6>
                  </div>
                  <div className="card-body">
                    {Object.entries(analytics.ageGroups || {}).map(([age, count]) => (
                      <div key={age} className="d-flex justify-content-between mb-2">
                        <span>{age} years</span>
                        <span className="badge bg-warning">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'families' && (
            <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #ddd' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
                <h6 style={{ margin: 0 }}>Families Data ({filteredData.length})</h6>
                <button className="btn btn-success btn-sm" onClick={exportData}>📥 Export Excel</button>
              </div>
              <div style={{ minWidth: '4000px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{ backgroundColor: '#343a40', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      {[
                        'ID', 'Head Name', 'Display Name', 'State', 'City', 'Block', 'Mobile', 'Members', 'Area Type',
                        'Country', 'Panchayat', 'Gram', 'Urban Area', 'Total Members', 'Popular Name', 'Family Mobile',
                        'Address', 'Mandir', 'Mandir Relation', 'Language', 'Family Type', 'House Type',
                        'Jati Prakar', 'Upjati Prakar', 'Gotra', 'Moor', 'Work Type', 'Work Area', 'Status', 'Reg Date'
                      ].map(header => (
                        <th key={header} style={{ padding: '8px', border: '1px solid #ddd', minWidth: '120px', textAlign: 'left' }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((family, idx) => (
                      <tr key={family.id} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white' }}>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.id}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.headName || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.displayName || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.state || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.city || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.block || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.mobile || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'center' }}>{family.members?.length || 0}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.areaType || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>India</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.panchayat || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.gram || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.urbanArea || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'center' }}>{family.basic?.Totalmember || family.members?.length || 0}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.displayName || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.mobile || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.address || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.Mandirtype || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.mandirRelation || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.language || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.familyType || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.houseType || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.jatiPrakar || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.upjatiPrakar || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.gotra || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.moor || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.workType || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.basic?.workArea || '-'}</td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            color: 'white',
                            backgroundColor: 
                              family.meta?.status === 'approved' ? '#28a745' :
                              family.meta?.status === 'hold' ? '#ffc107' :
                              family.meta?.status === 'rejected' ? '#dc3545' : '#6c757d'
                          }}>
                            {family.meta?.status || 'pending'}
                          </span>
                        </td>
                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{family.meta?.createdAt ? new Date(family.meta.createdAt).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {viewMode === 'members' && (
            <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #ddd' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
                <h6 style={{ margin: 0 }}>Members Data ({filteredData.length})</h6>
                <button className="btn btn-success btn-sm" onClick={exportData}>📥 Export Excel</button>
              </div>
              <div style={{ minWidth: '6000px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead style={{ backgroundColor: '#343a40', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      {[
                        'Family ID', 'Member Name', 'Family Head', 'Popular Name', 'Father/Husband', 'Relation', 'Gender',
                        'Member Mobile', 'Family Mobile', 'WhatsApp', 'Email', 'DOB', 'Aadhaar', 'Blood Group', 'Marital Status',
                        'Country', 'State', 'City', 'Block', 'Area Type', 'Panchayat', 'Gram', 'Urban Area', 'Family Address',
                        'Total Members', 'Language', 'Mandir', 'Mandir Relation', 'Jati Prakar', 'Upjati Prakar', 'Gotra', 'Moor',
                        'Family Work Type', 'Family Work Area', 'Family Type', 'House Type', 'Current Address', 'Birth Address',
                        'Education Status', 'Education State', 'Education District', 'Education Class', 'Education Subject',
                        'Work Type', 'Work State', 'Work District', 'Work Area', 'Work Area Name', 'Work Department', 'Work Position',
                        'Family Status', 'Registration Date'
                      ].map(header => (
                        <th key={header} style={{ padding: '6px', border: '1px solid #ddd', minWidth: '100px', textAlign: 'left' }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((member, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white' }}>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyId || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.name || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.headName || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyPopularName || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.fatherName || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.relation || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.gender || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.mobile || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyMobile || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.whatsapp || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.email || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.dob || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.aadhaar || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.bloodGroup || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.maritalStatus || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>India</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyState || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyCity || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyBlock || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyAreaType || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyPanchayat || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyGram || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyUrbanArea || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyAddress || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center' }}>{member.familyTotalMembers || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyLanguage || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyMandir || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyMandirRelation || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyJatiPrakar || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyUpjatiPrakar || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyGotra || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyMoor || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyWorkType || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyWorkArea || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyType || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.familyHouseType || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.currentAddress || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.birthAddress || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.educationStatus || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.educationState || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.educationDistrict || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.educationClass || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.educationSubject || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workType || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workState || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workDistrict || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workArea || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workAreaName || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workDepartment || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.workPosition || '-'}</td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                          <span style={{
                            padding: '2px 4px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: 'white',
                            backgroundColor: 
                              member.familyStatus === 'approved' ? '#28a745' :
                              member.familyStatus === 'hold' ? '#ffc107' :
                              member.familyStatus === 'rejected' ? '#dc3545' : '#6c757d'
                          }}>
                            {member.familyStatus || 'pending'}
                          </span>
                        </td>
                        <td style={{ padding: '4px', border: '1px solid #ddd' }}>{member.registrationDate ? new Date(member.registrationDate).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdvancedAnalytics;